import Component from '@glimmer/component';
import ENV from 'dashboard/config/environment';
import { getInitials } from 'dashboard/utils';
import { t } from 'ember-intl';

/** @see shoelace-theme.css */
const COLOUR_COUNT = 24;

interface UserAvatarSignature {
  Element: HTMLDivElement;
  Args: {
    imgSrc?: string;
    firstName?: string;
    lastName?: string;
    generic?: boolean;
    pending?: boolean;
    deactivated?: boolean;
    large?: boolean;
  };
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    UserAvatar: typeof UserAvatar;
  }
}

function unicodeSumFromString(str: string): number {
  return [...str].reduce((acc, value) => {
    const val: number | undefined = value.codePointAt(0);
    if (val) {
      return acc + val;
    } else {
      return acc;
    }
  }, 0);
}

export default class UserAvatar extends Component<UserAvatarSignature> {
  get defaultAvatarUrl(): string {
    return ENV.APP.defaultAvatarUrl;
  }

  get initials(): string {
    return getInitials(this.args.firstName || '', this.args.lastName || '');
  }

  get colorIndex(): number {
    const name = [this.args.firstName, this.args.lastName].join(' ');
    const index = unicodeSumFromString(name) % (COLOUR_COUNT - 1);
    return index;
  }

  get isGeneric() {
    return this.args.pending || this.args.deactivated;
  }

  get showInitials() {
    const hasSrc =
      this.args.imgSrc && this.args.imgSrc !== this.defaultAvatarUrl;
    return !hasSrc && !this.isGeneric;
  }

  get variantClasses() {
    const size = this.args.large ? 'lbx-avatar--large' : '';
    const base = `lbx-avatar ${size}`;

    if (this.isGeneric) {
      return `${base} lbx-avatar--generic`;
    } else {
      return `${base} lbx-avatar--initials lbx-avatar--initials-${this.colorIndex}`;
    }
  }

  <template>
    {{#if this.isGeneric}}
      <sl-avatar label='Generic user avatar' class='{{this.variantClasses}}'>
        {{#if @pending}}
          <sl-icon slot='icon' library='showbie' name='ellipsis'></sl-icon>
        {{else if @deactivated}}
          <sl-icon slot='icon' library='showbie' name='no-entry'></sl-icon>
        {{/if}}
      </sl-avatar>
    {{else if this.showInitials}}
      <sl-avatar
        label='Avatar with initials: {{this.initials}}'
        initials={{this.initials}}
        class='{{this.variantClasses}}'
      >
      </sl-avatar>
    {{else}}
      <sl-avatar
        label={{t "User's avatar"}}
        loading='lazy'
        image={{@imgSrc}}
        class='{{this.variantClasses}}'
      >
      </sl-avatar>
    {{/if}}
  </template>
}
